import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import dayjs from 'dayjs';

import { capitalize } from '../helpers/common';
import { sortDates, sortStrings } from '../helpers/filter';

import { DATE_FORMAT } from '../constants/common';
import { BLANK_FILTER_TEXT } from '../constants/livestock';
import { TABLE_CONFIG_COLUMNS } from '../constants/table-configurations';

import { COLUMN_SIZE } from '../components/AppTable/constants';
import StatusIndicator from '../components/StatusIndicator';

export const useColumns = ({ filteredInfo, columnFilterOptions }) => {
  return useMemo(() => {
    return [
      {
        title: 'Livestock ID',
        dataIndex: 'identifier',
        configColumnKey: TABLE_CONFIG_COLUMNS.LIVESTOCK_ID,
        ellipsis: true,
        fixed: 'left',
        filteredValue: filteredInfo.identifier || null,
        width: COLUMN_SIZE.MD,
        searchable: true,
        sortDirections: ['ascend', 'descend'],
        render: (value, record) => <Link to={`/tracking/${record.id}`}>{value}</Link>,
        sorter: (a, b) => sortStrings(a.identifier, b.identifier)
      },
      {
        title: 'Mgmt Tag ID',
        dataIndex: 'eartag_management_id',
        configColumnKey: TABLE_CONFIG_COLUMNS.MGMT_TAG_ID,
        ellipsis: true,
        filteredValue: filteredInfo.eartag_management_id || null,
        width: COLUMN_SIZE.XL,
        searchable: true,
        sortDirections: ['ascend', 'descend'],
        sorter: (a, b) => sortStrings(a.identifier, b.identifier),
        rules: [{ required: true, message: 'Mgmt Tag ID is required.' }]
      },
      {
        title: 'Sex',
        dataIndex: 'sex',
        configColumnKey: TABLE_CONFIG_COLUMNS.SEX,
        ellipsis: true,
        filteredValue: filteredInfo.sex || null,
        width: COLUMN_SIZE.SM,
        sortDirections: ['ascend', 'descend'],
        filters: columnFilterOptions?.sex,
        onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.sex : record?.sex === value),
        render: value => capitalize(value),
        sorter: (a, b) => sortStrings(a.sex, b.sex)
      },
      {
        title: 'Colour',
        dataIndex: 'colour',
        configColumnKey: TABLE_CONFIG_COLUMNS.COLOUR,
        ellipsis: true,
        filteredValue: filteredInfo.colour || null,
        width: COLUMN_SIZE.SM,
        searchable: true,
        sortDirections: ['ascend', 'descend'],
        sorter: (a, b) => sortStrings(a.colour, b.colour)
      },
      {
        title: 'Breed',
        dataIndex: 'breedName',
        configColumnKey: TABLE_CONFIG_COLUMNS.BREED,
        ellipsis: true,
        filteredValue: filteredInfo.breedName || null,
        width: COLUMN_SIZE.LG,
        sortDirections: ['ascend', 'descend'],
        filterSearch: true,
        filters: columnFilterOptions?.breeds,
        onFilter: (value, record) =>
          value === BLANK_FILTER_TEXT ? !record?.breed?.display_name : record?.breed?.display_name === value,
        render: (value, record) => record?.breed?.display_name,
        sorter: (a, b) => sortStrings(a.breed?.display_name, b.breed?.display_name)
      },
      {
        title: 'Kraal Tag ID',
        dataIndex: 'tagIdentifier',
        configColumnKey: TABLE_CONFIG_COLUMNS.KRAAL_TAG_ID,
        ellipsis: true,
        filteredValue: filteredInfo.tagIdentifier || null,
        width: COLUMN_SIZE.LG,
        searchable: true,
        sortDirections: ['ascend', 'descend'],
        onFilter: (value, record) => {
          const searchField = record?.tag?.diagri_id || 'Unlinked';

          return searchField.toString().toLowerCase().includes(value.toLowerCase());
        },
        render: (value, record) =>
          record?.tag?.diagri_id ? <Link to={`/tag/${record?.tag?.id}`}>{record?.tag?.diagri_id}</Link> : 'Unlinked',
        sorter: (a, b) => sortStrings(a?.tag?.id, b?.tag?.id)
      },
      {
        title: 'Brand',
        dataIndex: 'brand',
        configColumnKey: TABLE_CONFIG_COLUMNS.BRAND,
        ellipsis: true,
        filteredValue: filteredInfo.brand || null,
        searchable: true,
        sortDirections: ['ascend', 'descend'],
        width: COLUMN_SIZE.MD,
        sorter: (a, b) => sortStrings(a.brand, b.brand)
      },
      {
        title: 'Farm',
        dataIndex: 'farm',
        configColumnKey: TABLE_CONFIG_COLUMNS.FARM,
        ellipsis: true,
        filteredValue: filteredInfo.farm || null,
        width: COLUMN_SIZE.XL,
        sortDirections: ['ascend', 'descend'],
        filterSearch: true,
        filters: columnFilterOptions?.farms,
        onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.farm?.name : record?.farm?.id === value),
        render: (value, record) => record?.farm?.name || '',
        sorter: (a, b) => sortStrings(a?.farm?.name, b?.farm?.name)
      },
      {
        title: 'Assigned Pasture',
        dataIndex: 'assignedGeofence',
        configColumnKey: TABLE_CONFIG_COLUMNS.ASSIGNED_GEOFENCE,
        ellipsis: true,
        filteredValue: filteredInfo.assignedGeofence || null,
        width: COLUMN_SIZE.LG,
        sortDirections: ['ascend', 'descend'],
        filterSearch: true,
        filters: columnFilterOptions?.locations,
        onFilter: (value, record) =>
          value === BLANK_FILTER_TEXT
            ? !record?.assigned_geofences?.length
            : record?.assigned_geofences?.includes(value),
        render: (value, record) => {
          const currentGeofence = record.geofences?.find(g => !g?.is_master);

          return currentGeofence ? (
            <Link to={`/pastures/${currentGeofence?.id}`}>{record.assigned_geofences?.at(0)}</Link>
          ) : (
            ''
          );
        },
        sorter: (a, b) => sortStrings(a.assigned_geofences.at(0), b.assigned_geofences.at(0))
      },
      {
        title: 'Last Reading',
        dataIndex: 'lastReading',
        configColumnKey: TABLE_CONFIG_COLUMNS.LAST_READING,
        ellipsis: true,
        filteredValue: filteredInfo.lastReading || null,
        width: COLUMN_SIZE.XL,
        searchable: true,
        sortDirections: ['ascend', 'descend'],
        onFilter: (value, record) => record?.tag?.timestamp_at?.includes(value.toLowerCase()),
        sorter: (a, b) => sortDates(a.tag?.timestamp_at, b.tag?.timestamp_at),
        render: (value, record) => {
          const lastReadingTime = record?.tag?.timestamp_at;

          return lastReadingTime ? dayjs(lastReadingTime).format(DATE_FORMAT.DATETIME) : null;
        }
      },
      {
        title: 'Connection Status',
        dataIndex: 'connectionStatus',
        configColumnKey: TABLE_CONFIG_COLUMNS.CONNECTION_STATUS,
        ellipsis: true,
        filteredValue: filteredInfo.connectionStatus || null,
        width: COLUMN_SIZE.XL,
        sortDirections: ['ascend', 'descend'],
        filters: columnFilterOptions?.connectionStatuses,
        onFilter: (value, record) =>
          value === BLANK_FILTER_TEXT ? !record?.tag?.status?.length : record?.tag?.status?.includes(value),
        sorter: (a, b) => sortStrings(a?.tag?.status, b?.tag?.status),
        render: (value, record) => {
          const status = record?.tag?.status;
          const id = record?.id;

          return (
            record && (
              <div className="d-flex align-items-center text-capitalize">
                <StatusIndicator key={id} status={status} />
                {status}
              </div>
            )
          );
        }
      }
    ];
  }, [
    columnFilterOptions?.breeds,
    columnFilterOptions?.connectionStatuses,
    columnFilterOptions?.farms,
    columnFilterOptions?.locations,
    columnFilterOptions?.sex,
    filteredInfo.assignedGeofence,
    filteredInfo.brand,
    filteredInfo.breedName,
    filteredInfo.colour,
    filteredInfo.connectionStatus,
    filteredInfo.eartag_management_id,
    filteredInfo.farm,
    filteredInfo.identifier,
    filteredInfo.lastReading,
    filteredInfo.sex,
    filteredInfo.tagIdentifier
  ]);
};
